import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LinearScaleRoundedIcon from "@mui/icons-material/LinearScaleRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import OpenWithRoundedIcon from "@mui/icons-material/OpenWithRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import type { FederatedPointerEvent } from "pixi.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { createEditorRenderer, type EditorRendererHandle } from "@/features/scene-editor/editor-renderer";
import { useEditorStore } from "@/features/scene-editor/editor-store";
import {
  type FactoryScene,
  MAX_OBJECTS_PER_KIND,
  type ObjectKind,
  type SceneObject,
  tileEquals,
} from "@/features/simulation/public";
import { ISO_TILE_H, ISO_TILE_W, MAP_COLS, MAP_ROWS, type TilePos } from "@/lib/pixi/isoCoords";
import { PixiStage, type PixiStageHandle } from "@/lib/pixi/PixiStage";

/**
 * エディタ用 Pixi キャンバス。
 *
 * 操作モデル:
 *  - 左クリック (空白): 選択解除 (placement モード時は配置, route 編集中は waypoint 追加)
 *  - 左クリック (オブジェクト): 選択
 *  - 左ドラッグ (オブジェクト): タイルにスナップして移動
 *  - 右クリック: コンテキストメニュー (追加 / 削除 / 経路編集 等)
 *  - 右ドラッグ / 中ドラッグ: カメラパン (camera.ts 側で処理)
 *  - ホイール: ズーム
 */

const KIND_DISPLAY: Record<ObjectKind, { label: string; Icon: typeof LinearScaleRoundedIcon }> = {
  conveyor: { label: "コンベア", Icon: LinearScaleRoundedIcon },
  press: { label: "プレス機", Icon: PrecisionManufacturingRoundedIcon },
  arm: { label: "アーム", Icon: SmartToyRoundedIcon },
  transport: { label: "搬送ロボ", Icon: LocalShippingRoundedIcon },
  sensor: { label: "センサー", Icon: SensorsRoundedIcon },
};

interface ContextMenuState {
  /** ブラウザ座標 (clientX/Y) */
  anchor: { x: number; y: number };
  tile: TilePos;
  hit: { kind: ObjectKind; id: string } | null;
}

export function EditorCanvas() {
  const { draft, selection, placement } = useEditorStore(
    useShallow((s) => ({ draft: s.draft, selection: s.selection, placement: s.placement })),
  );
  const setSelection = useEditorStore((s) => s.setSelection);
  const placeObject = useEditorStore((s) => s.placeObject);
  const moveObject = useEditorStore((s) => s.moveObject);
  const removeObject = useEditorStore((s) => s.removeObject);
  const beginRouteEdit = useEditorStore((s) => s.beginRouteEdit);
  const routeEditing = useEditorStore((s) => s.routeEditing);

  const rendererRef = useRef<EditorRendererHandle | null>(null);
  const dragStateRef = useRef<{
    ref: { kind: ObjectKind; id: string };
    lastTile: TilePos;
    /** 移動が一度でも発生したか (true ならクリック扱いせず確実に drag-move として扱う) */
    moved: boolean;
  } | null>(null);

  /**
   * 右ボタン press 時のスクリーン座標を記録し、release 時に同じ位置 (閾値内) ならコンテキストメニューを開く。
   * ドラッグでパンしているときは menu を開かない。
   */
  const rightDownRef = useRef<{
    clientX: number;
    clientY: number;
    tile: TilePos;
  } | null>(null);

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  const onReady = useCallback(
    (handle: PixiStageHandle) => {
      const renderer = createEditorRenderer(handle.layers);
      rendererRef.current = renderer;

      // 初回描画
      renderer.render(
        useEditorStore.getState().draft,
        computeSelectionTile(useEditorStore.getState().draft, useEditorStore.getState().selection),
        useEditorStore.getState().routeEditing?.partial ?? null,
      );

      handle.app.stage.eventMode = "static";
      handle.app.stage.hitArea = handle.app.screen;

      const tileFromEvent = (e: FederatedPointerEvent): TilePos | null => {
        const local = handle.layers.world.toLocal(e.global);
        const tile = screenToTileRounded(local.x, local.y);
        if (tile.col < 0 || tile.col >= MAP_COLS || tile.row < 0 || tile.row >= MAP_ROWS) return null;
        return tile;
      };

      const onPointerDown = (event: FederatedPointerEvent) => {
        const native = event.nativeEvent as PointerEvent;
        // 右ボタン: 右ドラッグのパンと右クリックのメニューを区別するため、
        // press 時の位置と tile を保存し、pointerup で再判定する。
        if (event.button === 2) {
          const tile = tileFromEvent(event);
          if (!tile) return;
          rightDownRef.current = { clientX: native.clientX, clientY: native.clientY, tile };
          return;
        }
        if (event.button !== 0) return;

        const tile = tileFromEvent(event);
        if (!tile) return;
        const editor = useEditorStore.getState();

        if (editor.routeEditing) {
          editor.appendRouteWaypoint(tile);
          return;
        }
        if (editor.placement) {
          placeObject(editor.placement, tile);
          return;
        }

        const hit = findObjectAtTile(editor.draft, tile);
        if (hit) {
          setSelection({ kind: hit.kind as ObjectKind, id: hit.id });
          // 左ドラッグでの移動を開始 (上下左右 1 タイル動いた瞬間から moveObject)
          dragStateRef.current = { ref: hit, lastTile: tile, moved: false };
        } else {
          setSelection(null);
        }
      };

      const onPointerMove = (event: FederatedPointerEvent) => {
        const drag = dragStateRef.current;
        if (!drag) return;
        const tile = tileFromEvent(event);
        if (!tile) return;
        if (tile.col === drag.lastTile.col && tile.row === drag.lastTile.row) return;
        drag.lastTile = tile;
        drag.moved = true;
        moveObject(drag.ref, tile);
      };

      const onPointerUp = (event: FederatedPointerEvent) => {
        // 左ボタン release: ドラッグ状態をクリア
        if (event.button === 0) {
          dragStateRef.current = null;
          return;
        }
        // 右ボタン release: ドラッグ判定 (移動量 < 閾値 ならクリック → メニュー)
        if (event.button === 2 && rightDownRef.current) {
          const native = event.nativeEvent as PointerEvent;
          const dx = native.clientX - rightDownRef.current.clientX;
          const dy = native.clientY - rightDownRef.current.clientY;
          const dist = Math.hypot(dx, dy);
          const startTile = rightDownRef.current.tile;
          rightDownRef.current = null;
          // 4px 以内ならクリックとみなしメニューを開く
          if (dist < 4) {
            const hit = findObjectAtTile(useEditorStore.getState().draft, startTile);
            setContextMenu({
              anchor: { x: native.clientX, y: native.clientY },
              tile: startTile,
              hit,
            });
          }
        }
      };

      handle.app.stage.on("pointerdown", onPointerDown);
      handle.app.stage.on("pointermove", onPointerMove);
      handle.app.stage.on("pointerup", onPointerUp);
      handle.app.stage.on("pointerupoutside", onPointerUp);

      return () => {
        handle.app.stage.off("pointerdown", onPointerDown);
        handle.app.stage.off("pointermove", onPointerMove);
        handle.app.stage.off("pointerup", onPointerUp);
        handle.app.stage.off("pointerupoutside", onPointerUp);
        renderer.dispose();
        rendererRef.current = null;
      };
    },
    [placeObject, setSelection, moveObject],
  );

  // draft / selection / routeEditing 変化時に再描画
  useEffect(() => {
    rendererRef.current?.render(draft, computeSelectionTile(draft, selection), routeEditing?.partial ?? null);
  }, [draft, selection, routeEditing]);

  const closeMenu = () => setContextMenu(null);

  const handleAddObject = (kind: ObjectKind) => {
    if (!contextMenu) return;
    placeObject(kind, contextMenu.tile);
    closeMenu();
  };

  const handleRemove = () => {
    if (!contextMenu?.hit) return;
    removeObject(contextMenu.hit);
    closeMenu();
  };

  const handleSelectInPanel = () => {
    if (!contextMenu?.hit) return;
    setSelection(contextMenu.hit);
    closeMenu();
  };

  const handleEditRoute = () => {
    if (!contextMenu?.hit || contextMenu.hit.kind !== "transport") return;
    beginRouteEdit(contextMenu.hit.id);
    setSelection(contextMenu.hit);
    closeMenu();
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        background: "#f4efe6",
        cursor: placement || routeEditing ? "crosshair" : "default",
      }}
    >
      <PixiStage onReady={onReady} initialZoom={2.5} panButtons={{ left: false, middle: true, right: true }} />
      <Menu
        open={contextMenu !== null}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.anchor.y, left: contextMenu.anchor.x } : undefined}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {contextMenu?.hit ? (
          [
            <MenuItem key="select" onClick={handleSelectInPanel}>
              <ListItemIcon>
                <OpenWithRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={`${contextMenu.hit.id} を選択`}
                secondary={KIND_DISPLAY[contextMenu.hit.kind].label}
              />
            </MenuItem>,
            contextMenu.hit.kind === "transport" ? (
              <MenuItem key="route" onClick={handleEditRoute}>
                <ListItemIcon>
                  <RouteRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="経路を編集" />
              </MenuItem>
            ) : null,
            <Divider key="d1" />,
            <MenuItem key="remove" onClick={handleRemove}>
              <ListItemIcon>
                <DeleteOutlineRoundedIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="削除" />
            </MenuItem>,
          ]
        ) : contextMenu ? (
          <AddSubMenu draft={draft} onAdd={handleAddObject} />
        ) : null}
      </Menu>
    </Box>
  );
}

interface AddSubMenuProps {
  draft: FactoryScene;
  onAdd: (kind: ObjectKind) => void;
}

/** 空白タイル右クリック時: 配置可能な kind 一覧 (上限到達は disable) */
function AddSubMenu({ draft, onAdd }: AddSubMenuProps): React.ReactElement[] {
  const counts: Record<ObjectKind, number> = {
    conveyor: draft.conveyors.length,
    press: draft.presses.length,
    arm: draft.arms.length,
    transport: draft.transports.length,
    sensor: draft.sensors.length,
  };
  return (Object.keys(KIND_DISPLAY) as ObjectKind[]).map((kind) => {
    const { label, Icon } = KIND_DISPLAY[kind];
    const atLimit = counts[kind] >= MAX_OBJECTS_PER_KIND[kind];
    return (
      <MenuItem key={kind} onClick={() => onAdd(kind)} disabled={atLimit}>
        <ListItemIcon>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={`${label} を追加`} secondary={`${counts[kind]}/${MAX_OBJECTS_PER_KIND[kind]}`} />
      </MenuItem>
    );
  });
}

/** 選択オブジェクトの代表タイル (ハイライト用) */
function computeSelectionTile(draft: FactoryScene, sel: { kind: ObjectKind; id: string } | null): TilePos | null {
  if (!sel) return null;
  const obj = findObjectById(draft, sel.kind, sel.id);
  if (!obj) return null;
  switch (obj.kind) {
    case "conveyor":
      return obj.path[0] ?? null;
    case "press":
    case "sensor":
      return obj.position;
    case "arm":
      return obj.position;
    case "transport":
      return obj.parking;
  }
}

/** タイル位置にあるオブジェクトを発見 (近距離優先で sensor/arm/press/transport/conveyor 順) */
function findObjectAtTile(scene: FactoryScene, tile: TilePos): { kind: ObjectKind; id: string } | null {
  for (const o of scene.sensors) if (tileEquals(o.position, tile)) return { kind: "sensor", id: o.id };
  for (const o of scene.arms) {
    if (tileEquals(o.position, tile) || tileEquals(o.pickFromTile, tile) || tileEquals(o.placeToTile, tile))
      return { kind: "arm", id: o.id };
  }
  for (const o of scene.presses) if (tileEquals(o.position, tile)) return { kind: "press", id: o.id };
  for (const o of scene.transports) {
    if (tileEquals(o.parking, tile) || tileEquals(o.unloading, tile)) return { kind: "transport", id: o.id };
    if (o.route.some((r) => tileEquals(r, tile))) return { kind: "transport", id: o.id };
  }
  for (const o of scene.conveyors) {
    if (o.path.some((p) => tileEquals(p, tile))) return { kind: "conveyor", id: o.id };
  }
  return null;
}

function findObjectById(scene: FactoryScene, kind: ObjectKind, id: string): SceneObject | null {
  switch (kind) {
    case "conveyor":
      return scene.conveyors.find((o) => o.id === id) ?? null;
    case "press":
      return scene.presses.find((o) => o.id === id) ?? null;
    case "arm":
      return scene.arms.find((o) => o.id === id) ?? null;
    case "transport":
      return scene.transports.find((o) => o.id === id) ?? null;
    case "sensor":
      return scene.sensors.find((o) => o.id === id) ?? null;
  }
}

/**
 * 等角スクリーン座標 (world ローカル) からタイル座標へ逆変換し四捨五入。
 */
function screenToTileRounded(x: number, y: number): TilePos {
  const col = x / ISO_TILE_W + y / ISO_TILE_H;
  const row = y / ISO_TILE_H - x / ISO_TILE_W;
  return { col: Math.round(col), row: Math.round(row) };
}
